import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import StepContent from "@material-ui/core/StepContent";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import { useHistory } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListSubheader from "@material-ui/core/ListSubheader";
import HelpIcon from "@material-ui/icons/Help";
import Tooltip from "@material-ui/core/Tooltip";
import moment from "moment-timezone";
import { MuiPickersUtilsProvider, DateTimePicker } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import {
  FormControl,
  FormControlLabel,
  Radio,
  IconButton,
  InputAdornment,
  Checkbox,
  ListItemText,
  FormGroup,
  DialogContentText,
} from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import uuidv4 from "uuid/v4";

import LoadingButton from "../../Buttons/Loading";
import * as constants from "../../../constants";
import * as actions from "../../../store/actions";
import {
  queryShifts,
  createOpenShift,
  createShift,
} from "../../../utils/appsyncOperations";
import { isUserRole } from "../../../utils/user";
import ShiftTemplatePicker from "../ShiftTemplatePicker";
import getAllDivisionUsersById from "../../../utils/getAllDivisionUsersById";
import getUserLevelAvatar from "../../../utils/getUserLevelAvatar";
import useCalendarBounds from "../../../hooks/useCalendarBounds";
import PostOrders from "../../Lists/ImprovedPostOrders";
import UsersSearchDialog from "../UsersSearch";
import { init } from "../../../store/features/postOrders";

const CreateShiftStepper = ({ isOpen, onClose, divisionId }) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [loadingScheduledHours, setLoadingScheduledHours] = useState(false);
  const [openShift, setOpenShift] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [showShiftTemplatePicker, setShowShiftTemplatePicker] = useState(false);
  const timezoneGuess = moment.tz.guess(true);
  const [timezone, setTimezone] = useState(timezoneGuess);
  const [startMoment, setStartMoment] = useState(() => moment());
  const [endMoment, setEndMoment] = useState(() => moment());
  const serviceProviderId = useSelector(
    (state) => state.serviceProvider.serviceProvider.serviceProviderId
  );
  const postOrders = useSelector((state) => state.postOrders.postOrders);
  const appUserId = useSelector((state) => state.auth.user.username);
  const enqueueSnackbar = useSnackbar().enqueueSnackbar;
  const dispatch = useDispatch();
  const history = useHistory();
  const [userId, setUserId] = useState(null);
  const [levels, setLevels] = useState({
    one: false,
    two: false,
    three: false,
  });
  const [scheduledHoursByUserId, setScheduledHoursByUserId] = useState({});
  const calendarBounds = useCalendarBounds(
    startMoment.format(constants.calendar.DATE_FORMAT)
  );
  const users = useSelector((state) =>
    getAllDivisionUsersById({ division: state.divisions.byId[divisionId] })
  );

  useEffect(() => {
    dispatch(init({ postOrders: [] }));
  }, [isOpen]);

  const steps = [
    <span className={classes.stepperHelpContainer}>
      Template
      <Tooltip title={constants.helpText.shiftTemplate}>
        <HelpIcon
          className={classes.stepperHelp}
          color={"action"}
          fontSize={"small"}
        />
      </Tooltip>
    </span>,
    "Date and Time",
    "Assignee",
    "Post Orders",
  ];

  const user = users[userId];

  const handleAssignee = () => {
    if (
      openShift &&
      levels.one === false &&
      levels.two === false &&
      levels.three === false
    ) {
      enqueueSnackbar("You must select at least one level.", {
        variant: "error",
      });
    } else if (!openShift && !userId) {
      enqueueSnackbar("You must select a person.", { variant: "error" });
    } else {
      handleNext();
    }
  };

  const handleCreateShift = async () => {
    if (startMoment.isAfter(endMoment)) {
      enqueueSnackbar("Start time cannot be after the end time.", {
        variant: "error",
      });
    } else if (openShift && !levels.one && !levels.two && !levels.three) {
      enqueueSnackbar("At least one officer level must be specified");
    } else {
      let requiredLevels;
      if (openShift) {
        requiredLevels = [];
        if (levels.one) {
          requiredLevels.push(constants.officerLevels.LEVEL_1.value);
        }
        if (levels.two) {
          requiredLevels.push(constants.officerLevels.LEVEL_2.value);
        }
        if (levels.three) {
          requiredLevels.push(constants.officerLevels.LEVEL_3.value);
        }
      }
      const _createShift = async () => {
        if (openShift) {
          return await createOpenShift({
            divisionId,
            shiftId: uuidv4(),
            startTime: startMoment.valueOf(),
            endTime: endMoment.valueOf(),
            createdBy: appUserId,
            requiredLevels,
            serviceProviderId,
            timezone,
            postOrders,
          });
        } else {
          return await createShift({
            divisionId,
            shiftId: uuidv4(),
            startTime: startMoment.valueOf(),
            endTime: endMoment.valueOf(),
            userId,
            createdBy: appUserId,
            serviceProviderId,
            timezone,
            postOrders,
          });
        }
      };
      setIsLoading(true);
      try {
        const createdShift = await _createShift();
        dispatch(actions.shifts.setShift(createdShift));
        onClose();
      } catch (error) {
        console.error(error);
        enqueueSnackbar(error, { variant: "error" });
      }
      setIsLoading(false);
    }
  };

  const getNextHandler = () => {
    switch (activeStep) {
      case 0:
        return handleNext();
      case 1:
        return handleNext();
      case 2:
        return handleAssignee();
      case 3:
        return handleCreateShift();
      default:
        return () => {};
    }
  };

  const handleUserSearch = () => setShowUserSearch(true);

  const closeUsersSearch = () => setShowUserSearch(false);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderStepButtons = () => {
    return (
      <div className={classes.actionsContainer}>
        <>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            className={classes.button}
          >
            Back
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={getNextHandler}
            className={classes.button}
            isLoading={isLoading}
          >
            {activeStep === steps.length - 1 ? "Finish" : "Next"}
          </LoadingButton>
        </>
      </div>
    );
  };

  const renderDateAndTime = () => {
    return (
      <List>
        <ListItem>
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  fullWidth
                  label="Time zone"
                  select
                  variant="filled"
                >
                  {[timezoneGuess, ...Object.values(constants.timezones)].map(
                    (timezone) => (
                      <MenuItem key={timezone} value={timezone}>
                        {timezone}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <DateTimePicker
                  value={startMoment}
                  onChange={(moment) => setStartMoment(moment.seconds(0))}
                  ampm={false}
                  format={constants.moment.format}
                  disablePast
                  fullWidth
                  inputVariant="filled"
                  label="Start"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <DateTimePicker
                  value={endMoment}
                  onChange={(moment) => setEndMoment(moment.seconds(0))}
                  ampm={false}
                  format={constants.moment.format}
                  disablePast
                  fullWidth
                  inputVariant="filled"
                  label="End"
                />
              </Grid>
            </Grid>
          </MuiPickersUtilsProvider>
        </ListItem>
        <ListSubheader>Duration</ListSubheader>
        <ListItem>
          <ListItemText>{`${duration.toFixed(2)} hrs`}</ListItemText>
        </ListItem>
        <ListItem>{renderStepButtons()}</ListItem>
      </List>
    );
  };

  const renderTemplatePicker = () => {
    return (
      <List>
        <ListItem>
          <Button color={"primary"} onClick={openShiftTemplatePicker}>
            Search for shift template
          </Button>
        </ListItem>
        <ListItem>
          <Button
            variant="contained"
            color="primary"
            onClick={getNextHandler}
            className={classes.button}
          >
            Skip
          </Button>
        </ListItem>
      </List>
    );
  };

  const renderPostOrders = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <PostOrders isEditable={true} timezone={timezone} />
        </Grid>
        <Grid item xs={12}>
          {renderStepButtons()}
        </Grid>
      </Grid>
    );
  };

  const renderAssignee = () => {
    return (
      <List>
        <ListItem>
          <FormControl component="fieldset">
            <FormControlLabel
              checked={!openShift}
              onChange={(event) => setOpenShift(!event.target.checked)}
              control={<Radio color="secondary" />}
              label="Assign to an employee"
            />
          </FormControl>
        </ListItem>
        <div className={classes.radioContent}>
          <ListItem>
            <TextField
              value={user !== undefined ? user.displayName : ""}
              onClick={handleUserSearch}
              disabled={openShift || isLoading}
              fullWidth
              label="Person"
              InputProps={{
                endAdornment:
                  user !== undefined && !openShift ? (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          setUserId(null);
                        }}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                readOnly: true,
              }}
              variant="filled"
            />
          </ListItem>
          <ListItem>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <List>
                  <ListSubheader>Officer level</ListSubheader>
                  <ListItem>
                    <ListItemText>
                      {user !== undefined
                        ? getUserLevelAvatar(user.officerLevel)
                        : "No user selected"}
                    </ListItemText>
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={5}>
                <List>
                  <ListSubheader>
                    {`Scheduled hours (${moment(
                      calendarBounds.week.startTime
                    ).format("MMM D")} - ${moment(
                      calendarBounds.week.endTime
                    ).format("MMM D")})`}
                  </ListSubheader>
                  <ListItem>
                    <ListItemText>
                      {scheduledHours ? scheduledHours : "No user selected"}
                    </ListItemText>
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={4}>
                <List>
                  <ListSubheader>Hours including this shift</ListSubheader>
                  <ListItem>
                    <ListItemText>
                      {potentialHours ? potentialHours : "No user selected"}
                    </ListItemText>
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </ListItem>
        </div>
        <ListItem>
          <FormControl component="fieldset">
            <FormControlLabel
              checked={openShift}
              onChange={(event) => setOpenShift(event.target.checked)}
              control={<Radio color="secondary" />}
              label="Create an open shift"
            />
          </FormControl>
        </ListItem>
        <ListItem className={classes.radioContent}>
          <FormControl
            className={classes.formControl}
            component="fieldset"
            disabled={!openShift}
            fullWidth
          >
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={levels.one}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setLevels((prevState) => ({
                        ...prevState,
                        one: checked,
                      }));
                    }}
                    value="one"
                  />
                }
                label="Level 1"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={levels.two}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setLevels((prevState) => ({
                        ...prevState,
                        two: checked,
                      }));
                    }}
                    value="two"
                  />
                }
                label="Level 2"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={levels.three}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setLevels((prevState) => ({
                        ...prevState,
                        three: checked,
                      }));
                    }}
                    value="three"
                  />
                }
                label="Level 3"
              />
            </FormGroup>
          </FormControl>
        </ListItem>
        <ListItem>{renderStepButtons()}</ListItem>
      </List>
    );
  };

  const closeShiftTemplatePicker = () => setShowShiftTemplatePicker(false);
  const openShiftTemplatePicker = () => setShowShiftTemplatePicker(true);
  const autofillForm = (template) => {
    if (template.userId) {
      setOpenShift(false);
      setUserId(template.userId);
    } else if (
      template.requiredLevels &&
      Array.isArray(template.requiredLevels)
    ) {
      setOpenShift(true);
      setLevels({
        one: template.requiredLevels.includes(
          constants.officerLevels.LEVEL_1.value
        ),
        two: template.requiredLevels.includes(
          constants.officerLevels.LEVEL_2.value
        ),
        three: template.requiredLevels.includes(
          constants.officerLevels.LEVEL_3.value
        ),
      });
    }
    if (template.time) {
      const { time } = template;
      const nextStartMoment = startMoment
        .clone()
        .startOf("day")
        .add(time.startHours, "hours")
        .add(time.startMinutes, "minutes");
      setStartMoment(nextStartMoment);
      const nextEndMoment = nextStartMoment
        .clone()
        .add(time.duration, "milliseconds");
      setEndMoment(nextEndMoment);
    }
    if (template.postOrders) {
      dispatch(init({ postOrders: template.postOrders }));
    }
    setTimezone(template.timezone);
    setShowShiftTemplatePicker(false);
    handleNext();
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderTemplatePicker();
      case 1:
        return renderDateAndTime();
      case 2:
        return renderAssignee();
      case 3:
        return renderPostOrders();
      default:
        return "Unknown step";
    }
  };

  React.useEffect(() => {
    getUserShifts();
  }, [isOpen, calendarBounds.week.startTime, calendarBounds.week.endTime]);

  const getUserShifts = () => {
    setLoadingScheduledHours(true);
    queryShifts({
      serviceProviderId,
      from: calendarBounds.week.startTime,
      to: calendarBounds.week.endTime,
    }).then(
      (shifts) => {
        setScheduledHoursByUserId(
          shifts
            .filter((shift) => {
              return (
                shift.userId &&
                shift.status !==
                  constants.shift.status.timeOffRequested.value &&
                shift.status !== constants.shift.status.timeOffApproved.value
              );
            })
            .reduce((accumulator, shift) => {
              const nextScheduledHoursByUserId = { ...accumulator };
              const hours =
                (shift.endTime - shift.startTime) / 3600000; /* one hour */
              nextScheduledHoursByUserId[shift.userId] =
                nextScheduledHoursByUserId[shift.userId] !== undefined
                  ? nextScheduledHoursByUserId[shift.userId] + hours
                  : hours;
              return nextScheduledHoursByUserId;
            }, {})
        );
        setLoadingScheduledHours(false);
      },
      (error) => {
        enqueueSnackbar(error, { variant: "error" });
        setLoadingScheduledHours(false);
      }
    );
  };

  let duration;
  const diff = endMoment.diff(startMoment);
  if (diff > 0) {
    duration = diff / 3600000;
  } else {
    duration = 0;
  }
  let scheduledHours;
  let potentialHours;
  if (user !== undefined) {
    if (loadingScheduledHours) {
      scheduledHours = "Calculating hours...";
    } else {
      const userScheduledHours =
        scheduledHoursByUserId[user.userId] !== undefined
          ? scheduledHoursByUserId[user.userId]
          : 0;
      scheduledHours = userScheduledHours.toFixed(2);
      potentialHours = `${(userScheduledHours + duration).toFixed(2)}`;
    }
  }

  const isAdmin = isUserRole({ admin: true });

  if (!isAdmin)
    return (
      <>
        <Dialog open={isOpen} onClose={onClose} maxWidth={"md"} fullWidth>
          <DialogTitle>Create shift</DialogTitle>
          <DialogContent>
            <DialogContentText>You are not the proper role.</DialogContentText>
          </DialogContent>
        </Dialog>
      </>
    );

  if (!divisionId)
    return (
      <>
        <Dialog open={isOpen} onClose={onClose} maxWidth={"md"} fullWidth>
          <DialogTitle>Create shift</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Select a division to continue.
            </DialogContentText>
          </DialogContent>
        </Dialog>
      </>
    );

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} maxWidth={"md"} fullWidth>
        <DialogTitle>Create Shift</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} orientation={"vertical"}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
                <StepContent>{getStepContent(index)}</StepContent>
              </Step>
            ))}
          </Stepper>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <ShiftTemplatePicker
        open={showShiftTemplatePicker}
        onClose={closeShiftTemplatePicker}
        onTemplateClick={autofillForm}
      />
      <UsersSearchDialog
        open={showUserSearch}
        onClose={closeUsersSearch}
        onUserClick={(userId) => {
          setUserId(userId);
          closeUsersSearch();
        }}
        users={Object.values(users).map((user) => {
          const scheduledHours = scheduledHoursByUserId[user.userId];
          if (scheduledHours !== undefined) {
            return {
              ...user,
              scheduledHours,
            };
          } else {
            return user;
          }
        })}
        week={`${moment(calendarBounds.week.startTime).format(
          "MMM D"
        )} - ${moment(calendarBounds.week.endTime).format("MMM D")}`}
      />
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  stepperHelpContainer: {
    display: "flex",
    flex: 1,
    justifyContent: "flex-start",
    alignItems: "center",
  },
  stepperHelp: {
    marginLeft: theme.spacing(1),
  },
  button: {
    marginRight: theme.spacing(1),
  },
  actionsContainer: {
    marginTop: theme.spacing(2),
  },
  resetContainer: {
    padding: theme.spacing(3),
  },
  divisionSelectionText: {
    marginBottom: theme.spacing(1),
  },
  radioContent: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
}));

export default CreateShiftStepper;
